import type Guid from "_shared/guids/guid";
import { ResourceType } from "permissions/resources/permission-resource-types";

/**
 * Where one record's access lives: `/permissions/records/{domain}/{id}`, with the domain's plural slug
 * (`docs/design/permissions/PERMISSIONS_UI_DESIGN.md`, "Routes and gates"). Conversations came
 * first (#6171); documents, proposals and work agreements are on the same page (#6172); calendar
 * events, organization agreements and both template kinds followed (#6174). A proposal request had a
 * page too, until proposal requests were removed (#6469).
 */

/** The record domains the page draws, by slug, with the per-record kind each one's grants are of. */
export const RECORD_DOMAINS = {
  forums: ResourceType.ForumAccess,
  documents: ResourceType.DocumentAccess,
  proposals: ResourceType.ProposalAccess,
  "work-agreements": ResourceType.WorkAgreementAccess,
  "calendar-events": ResourceType.CalendarEventAccess,
  "organization-agreements": ResourceType.EntityAgreementAccess,
  "fee-schedule-templates": ResourceType.FeeScheduleTemplateAccess,
  "team-templates": ResourceType.TeamTemplateAccess,
} as const;

export type RecordDomain = keyof typeof RECORD_DOMAINS;

/**
 * The domains with a server Check access read: "can this person open it, and which rule decides?".
 * Conversations, documents, proposals and work agreements came first (#6190); calendar events,
 * organization agreements and both template kinds followed (#6832), so every domain the page draws
 * has one. A domain added to `RECORD_DOMAINS` is not listed here until its business center answers.
 */
const CHECKED_RECORD_DOMAINS = [
  "forums",
  "documents",
  "proposals",
  "work-agreements",
  "calendar-events",
  "organization-agreements",
  "fee-schedule-templates",
  "team-templates",
] as const satisfies readonly RecordDomain[];

export type CheckedRecordDomain = (typeof CHECKED_RECORD_DOMAINS)[number];

/** Whether the server answers Check access for this domain's records. */
export function hasAccessCheck(domain: RecordDomain): domain is CheckedRecordDomain {
  return (CHECKED_RECORD_DOMAINS as readonly RecordDomain[]).includes(domain);
}

/** Whether a domain's records have conversations and documents of their own, listed below them: a proposal's and a work agreement's. */
export function hasRelatedRecords(domain: RecordDomain): boolean {
  return domain === "proposals" || domain === "work-agreements";
}

/** The domain whose records a per-record kind's grants are on, or nothing for a kind the page does not draw. */
export function recordDomainOf(resourceType: string): RecordDomain | undefined {
  return (Object.keys(RECORD_DOMAINS) as RecordDomain[]).find((domain) => RECORD_DOMAINS[domain] === resourceType);
}

export function isRecordDomain(slug: string | undefined): slug is RecordDomain {
  return slug !== undefined && Object.hasOwn(RECORD_DOMAINS, slug);
}

/** What a page linking here passes in its location state, so the record page can send the reader back. */
export type RecordPermissionsFrom = Readonly<{ pathname: string; search?: string; label?: string }>;

export type RecordPermissionsState = Readonly<{ from?: RecordPermissionsFrom }>;

/** Reads where the reader came from out of a location's state, whatever another page put there. */
export function recordPermissionsFrom(state: unknown): RecordPermissionsFrom | undefined {
  if (!state || typeof state !== "object" || !("from" in state)) return undefined;
  const from = (state as { from?: unknown }).from;
  if (!from || typeof from !== "object") return undefined;
  const { pathname, search, label } = from as Record<string, unknown>;
  if (typeof pathname !== "string" || !pathname.startsWith("/")) return undefined;
  return {
    pathname,
    search: typeof search === "string" ? search : undefined,
    label: typeof label === "string" ? label : undefined,
  };
}

export function recordPermissionsPath(domain: RecordDomain, recordId: Guid | string): string {
  const id = typeof recordId === "string" ? recordId : recordId.value;
  return `/permissions/records/${domain}/${id}`;
}
